/**
 * In-app prompt for the latest unopened energy data request. The server sends at most one request
 * a day (by push, email, or only here); this banner shows the newest one from the last 36 hours and
 * links to its Flux Agent voice-mode deep link. Opening it, or arriving through a notification link
 * carrying `?notification=<id>`, reports the open so it stops showing everywhere. "Not now" hides it
 * for this browser session only.
 *
 * @module apps/web/src/components/notifications/DataRequestBanner
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Mic, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { notificationsApi, type DataRequestNotification } from '@/lib/notificationsApi';
import { parseServerTime } from '@/lib/serverTime';

/** Requests older than this are not shown, in milliseconds (36 hours). */
const MAX_AGE_MS = 36 * 60 * 60 * 1000;
/** Minimum time between refreshes when the tab becomes visible, in milliseconds. */
const REFRESH_MS = 5 * 60 * 1000;
/** sessionStorage key prefix for requests hidden with "Not now". */
const DISMISSED_PREFIX = 'fluxability-data-request-dismissed:';

/** Parse a D1 UTC timestamp (`YYYY-MM-DD HH:MM:SS`) or ISO string into epoch milliseconds. */
function sentAtMs(value: string): number {
  return parseServerTime(value).getTime();
}

/** Read the session dismissal flag, treating unavailable storage as not dismissed. */
function isDismissed(id: string): boolean {
  try {
    return sessionStorage.getItem(DISMISSED_PREFIX + id) === '1';
  } catch {
    return false;
  }
}

/** Newest unopened, undismissed request from the last 36 hours, if any. */
function pick(requests: DataRequestNotification[]): DataRequestNotification | null {
  const latest = requests[0];
  if (!latest || latest.openedAt || isDismissed(latest.id)) return null;
  const age = Date.now() - sentAtMs(latest.sentAt);
  return Number.isFinite(age) && age <= MAX_AGE_MS ? latest : null;
}

/** Banner asking the latest data-request question, mounted once in the app shell. */
export function DataRequestBanner() {
  const location = useLocation();
  const navigate = useNavigate();
  const [current, setCurrent] = useState<DataRequestNotification | null>(null);
  const lastLoad = useRef(0);
  const reported = useRef(new Set<string>());

  const load = useCallback(async () => {
    lastLoad.current = Date.now();
    const response = await notificationsApi.dataRequests();
    if (response.success) setCurrent(pick(response.data?.requests ?? []));
  }, []);

  const markOpened = useCallback((id: string) => {
    if (reported.current.has(id)) return;
    reported.current.add(id);
    void notificationsApi.markOpened(id);
  }, []);

  useEffect(() => {
    void load();
    const onVisible = () => {
      if (document.visibilityState === 'visible' && Date.now() - lastLoad.current > REFRESH_MS)
        void load();
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
  }, [load]);

  // A tapped push notification opens its deep link with ?notification=<id>.
  useEffect(() => {
    const id = new URLSearchParams(location.search).get('notification');
    if (!id || !/^[A-Za-z0-9-]{1,64}$/.test(id)) return;
    markOpened(id);
    setCurrent(previous => (previous?.id === id ? null : previous));
  }, [location.search, markOpened]);

  if (!current) return null;

  const answer = () => {
    markOpened(current.id);
    setCurrent(null);
    navigate(current.url);
  };
  const dismiss = () => {
    try {
      sessionStorage.setItem(DISMISSED_PREFIX + current.id, '1');
    } catch {
      // Storage unavailable: hide for this page view only.
    }
    setCurrent(null);
  };

  return (
    <div
      role="region"
      aria-label="Energy question"
      className="bg-card mb-3 flex flex-col gap-2 rounded-md border px-3 py-2 text-sm shadow-sm sm:flex-row sm:items-center"
      data-testid="data-request-banner"
    >
      <p className="min-w-0 flex-1">{current.question}</p>
      <div className="flex shrink-0 items-center gap-2">
        <Button size="sm" onClick={answer}>
          <Mic className="mr-2 h-4 w-4" />
          Answer by voice
        </Button>
        <Button size="sm" variant="ghost" aria-label="Not now" onClick={dismiss}>
          <X className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );
}
